import React, { useState, useCallback } from 'react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import type { ZoneId, SectionCard as SectionCardType, SectionKey, SectionSpacing } from '../../types/editor'
import { ZONE_DEFINITIONS } from '../../types/editor'
import SectionCardComponent from './SectionCard'

interface ZoneEditorProps {
  zoneId: ZoneId
  cards: SectionCardType[]
  fixedItems?: SectionCardType[]
  onCardsChange: (zoneId: ZoneId, cards: SectionCardType[]) => void
  onSubSizeChange: (subKey: string, size: 'small' | 'medium' | 'large') => void
  onSliderChange: (subKey: string, value: number) => void
  onEdit: (key: SectionKey) => void
  sliderValues: Record<string, number>
  sectionSpacing?: Record<string, SectionSpacing>
  onSpacingChange?: (key: SectionKey, spacing: SectionSpacing) => void
}

const ZoneEditor: React.FC<ZoneEditorProps> = ({
  zoneId,
  cards,
  fixedItems = [],
  onCardsChange,
  onSubSizeChange,
  onSliderChange,
  onEdit,
  sliderValues,
  sectionSpacing,
  onSpacingChange,
}) => {
  const [collapsed, setCollapsed] = useState(false)
  const zone = ZONE_DEFINITIONS[zoneId]

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      if (!over || active.id === over.id) return

      const oldIndex = cards.findIndex(c => c.key === active.id)
      const newIndex = cards.findIndex(c => c.key === over.id)
      if (oldIndex === -1 || newIndex === -1) return

      const updated = [...cards]
      const [moved] = updated.splice(oldIndex, 1)
      updated.splice(newIndex, 0, moved)
      const reordered = updated.map((c, i) => ({ ...c, order: i }))
      onCardsChange(zoneId, reordered)
    },
    [cards, onCardsChange, zoneId],
  )

  const renderCard = (card: SectionCardType, immovable: boolean) => (
    <SectionCardComponent
      key={card.key}
      card={card}
      immovable={immovable}
      sliderValues={sliderValues}
      onSliderChange={onSliderChange}
      onSubSizeChange={onSubSizeChange}
      onEdit={onEdit}
      sectionSpacing={sectionSpacing?.[card.key]}
      onSpacingChange={onSpacingChange}
    />
  )

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50">
      <button
        type="button"
        onClick={() => setCollapsed(prev => !prev)}
        className="flex w-full items-center justify-between px-4 py-3 text-left text-sm font-semibold text-gray-700 hover:bg-gray-100 rounded-t-lg"
      >
        <span>{zone.label}</span>
        <span className="text-gray-400">{collapsed ? '▸' : '▾'}</span>
      </button>

      {!collapsed && (
        <div className="space-y-2 p-3">
          {fixedItems.map(card => renderCard(card, true))}

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={cards.map(c => c.key)}
              strategy={verticalListSortingStrategy}
            >
              {cards.map(card => renderCard(card, false))}
            </SortableContext>
          </DndContext>

          {cards.length === 0 && fixedItems.length === 0 && (
            <p className="py-4 text-center text-xs text-gray-400">No sections in this zone</p>
          )}
        </div>
      )}
    </div>
  )
}

export default ZoneEditor
